import { useState, useCallback } from 'react';
import { Phone, MessageCircle, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { cn } from '../../../lib/utils';

export type AttemptChannel = 'TELEFONE' | 'WHATSAPP' | 'EMAIL';
export type AttemptOutcome =
  | 'NAO_ATENDEU'
  | 'PEDIU_RETORNO'
  | 'CONTATO_REALIZADO'
  | 'CAIXA_POSTAL'
  | 'NUMERO_INVALIDO'
  | 'EMAIL_ENVIADO';

export interface AttemptFormData {
  channel: AttemptChannel;
  outcome: AttemptOutcome;
  notes: string;
}

interface RepescagemAttemptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AttemptFormData) => Promise<void>;
  isSubmitting: boolean;
  leadName: string;
  attemptNumber: number;
}

const CHANNEL_OPTIONS: { value: AttemptChannel; label: string; icon: React.ReactNode }[] = [
  { value: 'TELEFONE', label: 'Telefone', icon: <Phone className="h-4 w-4" /> },
  { value: 'WHATSAPP', label: 'WhatsApp', icon: <MessageCircle className="h-4 w-4" /> },
  { value: 'EMAIL', label: 'E-mail', icon: <Mail className="h-4 w-4" /> },
];

const OUTCOME_OPTIONS: { value: AttemptOutcome; label: string; channels: AttemptChannel[] }[] = [
  { value: 'NAO_ATENDEU', label: 'Não atendeu', channels: ['TELEFONE', 'WHATSAPP'] },
  { value: 'PEDIU_RETORNO', label: 'Pediu retorno', channels: ['TELEFONE', 'WHATSAPP', 'EMAIL'] },
  { value: 'CONTATO_REALIZADO', label: 'Contato realizado', channels: ['TELEFONE', 'WHATSAPP', 'EMAIL'] },
  { value: 'CAIXA_POSTAL', label: 'Caixa postal', channels: ['TELEFONE'] },
  { value: 'NUMERO_INVALIDO', label: 'Número inválido', channels: ['TELEFONE', 'WHATSAPP'] },
  { value: 'EMAIL_ENVIADO', label: 'E-mail enviado', channels: ['EMAIL'] },
];

export function RepescagemAttemptDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  leadName,
  attemptNumber,
}: RepescagemAttemptDialogProps) {
  const [channel, setChannel] = useState<AttemptChannel>('TELEFONE');
  const [outcome, setOutcome] = useState<AttemptOutcome>('NAO_ATENDEU');
  const [notes, setNotes] = useState('');

  const availableOutcomes = OUTCOME_OPTIONS.filter((opt) => opt.channels.includes(channel));

  const handleChannelChange = useCallback((newChannel: AttemptChannel) => {
    setChannel(newChannel);
    // Reset outcome if it's not compatible with new channel
    const compatible = OUTCOME_OPTIONS.filter((opt) => opt.channels.includes(newChannel));
    if (!compatible.find((opt) => opt.value === outcome)) {
      setOutcome(compatible[0]?.value ?? 'NAO_ATENDEU');
    }
  }, [outcome]);

  const handleSubmit = useCallback(async () => {
    await onSubmit({ channel, outcome, notes });
    // Reset form after successful submit
    setChannel('TELEFONE');
    setOutcome('NAO_ATENDEU');
    setNotes('');
  }, [channel, outcome, notes, onSubmit]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isSubmitting) {
      onOpenChange(isOpen);
      if (!isOpen) {
        setChannel('TELEFONE');
        setOutcome('NAO_ATENDEU');
        setNotes('');
      }
    }
  }, [isSubmitting, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova Tentativa de Contato</DialogTitle>
          <DialogDescription>
            Tentativa #{attemptNumber} — {leadName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Canal */}
          <div className="space-y-2">
            <Label>Canal</Label>
            <div className="flex gap-2">
              {CHANNEL_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChannelChange(opt.value)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors',
                    channel === opt.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Resultado */}
          <div className="space-y-2">
            <Label htmlFor="outcome-select">Resultado</Label>
            <Select value={outcome} onValueChange={(v) => setOutcome(v as AttemptOutcome)}>
              <SelectTrigger id="outcome-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableOutcomes.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="attempt-notes">Notas</Label>
            <Textarea
              id="attempt-notes"
              placeholder="Descreva o que aconteceu nessa tentativa..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Registrando...' : 'Registrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
